/***
 * PLP scroll restoration (back navigation from the PDP)
 *
 * The collection grid is rendered client-side from Nosto, so at the moment the browser would
 * normally restore the scroll position the document is still only placeholders and the restore
 * gets clamped to the top (or the bottom) of a very short page. history.scrollRestoration is
 * therefore set to 'manual' in the head (see snippets/plp-scroll-restoration.liquid) and we
 * re-apply the position ourselves once the tiles exist.
 *
 * Target, in order of preference:
 *   1. The product tile the user opened, parked under the sticky chrome (what they expect to
 *      see when they come back from its PDP).
 *   2. The tile that was at the top of the viewport, for a back navigation that didn't come
 *      from a PDP, or a reload.
 *   3. The raw pixel offset.
 ***/

const STORAGE_KEY = 'astr-plp-scroll';
const MAX_ENTRIES = 10;
const MAX_AGE = 30 * 60 * 1000; // Ignore anything older than 30 minutes
const RESTORE_DEADLINE = 12000; // Give up waiting for the tile after 12s
const SETTLE_QUIET_TIME = 1200; // Stop correcting once nothing has moved for 1.2s
const SAVE_DEBOUNCE = 300;
const TILE_GAP = 50; // Breathing room between the sticky chrome and the tile
const MAX_CORRECTIONS = 8; // Re-measure passes allowed per landing
const MAX_APPLIES = 12; // Scrolls allowed per landing, whatever triggered them
const LANDED_TOLERANCE = 2; // Close enough, in px, to stop correcting

const GRID_SELECTOR = '[data-collection-grid-container]';
const TILE_SELECTOR = '[data-product-tile][data-product-handle]';

// Anything pinned to the top of the viewport that the restored tile would end up underneath
const CHROME_SELECTORS = [
  '.shopify-section.header',
  '[data-countdown-banner]',
  '[data-filters-panel-v3]',
  '[data-collection-subcategories]',
];
const CHROME_MAX_TOP = 250; // Ignore pinned elements that aren't at the top of the viewport
const CHROME_MAX_RATIO = 0.4; // ...and anything tall enough to be an overlay rather than chrome

export default class PlpScrollRestore {
  enabled = false;
  isRestoring = false; // True while we own the scroll position

  pendingEntry = null; // The entry we still have to restore, null once applied or abandoned
  hasApplied = false; // At least one successful scroll for the current restore
  returnedFromHandle = ''; // Product whose PDP this back navigation came from
  corrections = 0; // Re-measure passes spent on the current landing
  applies = 0; // Scrolls applied on the current landing
  appliedTargets = []; // The last two offsets we scrolled to, to catch a pin/unpin flip
  observedRoot = null; // The grid we've attached the settle listeners to

  correctionFrame = null;
  deadlineTimer = null;
  quietTimer = null;
  saveTimer = null;
  resizeObserver = null;

  constructor() {
    if (!document.body?.classList.contains('template-plp')) {
      return;
    }

    this.enabled = true;

    // Before anything else: global.js rewrites 'current-page' on DOMContentLoaded, which is
    // moments away
    this.captureReturnOrigin();

    // Normally already set by the inline head snippet; harmless to repeat, and covers the case
    // where that snippet isn't rendered
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('pagehide', this.saveScrollPosition);
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('click', this.onTileClick, true);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    const nav_type = window.performance?.getEntriesByType?.('navigation')?.[0]?.type || '';

    // A fresh navigation to a collection should start at the top; only back/forward and
    // reloads are expected to come back to where the user was
    if (nav_type !== 'back_forward' && nav_type !== 'reload') {
      return;
    }

    const entry = this.readEntry();
    if (entry) {
      this.beginRestore(entry);
    }
  }

  /* *** Storage *** */

  pathKey() {
    return window.location.pathname;
  }

  readStore() {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  writeStore(store) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (e) {
      // Private mode / quota: scroll restoration is a nicety, never break the page for it
    }
  }

  readEntry() {
    const entry = this.readStore()[this.pathKey()];
    if (!entry || (Date.now() - (entry.ts || 0)) > MAX_AGE) {
      return null;
    }

    return entry;
  }

  updateEntry(changes) {
    const store = this.readStore();
    const key = this.pathKey();

    store[key] = Object.assign({}, store[key], changes, { ts: Date.now() });

    // Keep the store small: drop the oldest entries past the cap
    const keys = Object.keys(store);
    if (keys.length > MAX_ENTRIES) {
      keys
        .sort((a, b) => (store[b].ts || 0) - (store[a].ts || 0))
        .slice(MAX_ENTRIES)
        .forEach(stale_key => delete store[stale_key]);
    }

    this.writeStore(store);
  }

  /* *** Where we came from *** */

  /**
   * @param {string} url - Any URL or path
   *
   * @returns {string} The product handle it points at, or an empty string
   */
  static handleFromUrl(url) {
    if (!url || url.indexOf('/products/') === -1) {
      return '';
    }

    return url.split('/products/')[1].split(/[?#/]/)[0] || '';
  }

  /**
   * Note the page we've just come back from.
   *
   * Deliberately 'current-page' and not 'previous-page': global.js only rewrites those on
   * DOMContentLoaded, which a bfcache restore never fires, so 'previous-page' ends up a whole
   * navigation behind and points at the product opened *before* the last one. 'current-page'
   * is still the PDP we're returning from when we're constructed (global.js overwrites it
   * later, on DOMContentLoaded) and is left untouched entirely on a bfcache restore.
   *
   * Must therefore be captured up front, not read lazily at restore time.
   */
  captureReturnOrigin() {
    this.returnedFromHandle = PlpScrollRestore.handleFromUrl(window.localStorage.getItem('current-page') || '');
  }

  /* *** Measuring *** */

  static searchOverlayIsOpen() {
    return document.body?.classList.contains('has-overlay-search') === true;
  }

  getGridRoot() {
    return document.querySelector(GRID_SELECTOR) || document.querySelector('[data-product-tiles]');
  }

  findTile(handle) {
    if (!handle) return null;

    const escaped = (window.CSS && window.CSS.escape) ? window.CSS.escape(handle) : handle;
    return document.querySelector(`${GRID_SELECTOR} [data-product-tile][data-product-handle="${escaped}"]`);
  }

  static absoluteTop(element) {
    return element.getBoundingClientRect().top + window.scrollY;
  }

  /**
   * The viewport Y coordinate where the pinned chrome ends, i.e. the first line at which
   * content is actually visible.
   *
   * The header and the filter bar only become `position: fixed` once a class is toggled on
   * scroll (body.sticky-header, .filters-panel-v3.sticky), and pinning the filter bar also adds
   * padding to [data-products], which moves every tile. So this can't be measured before we
   * land — it is read live on each correction pass instead, and converges once we're scrolled
   * down and the chrome has settled into its pinned state.
   *
   * Measuring the bottom edge rather than summing heights means stacked chrome (header +
   * filter bar offset below it) is handled without double counting.
   *
   * @returns {number}
   */
  getChromeBottom() {
    let bottom = 0;

    CHROME_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        const styles = window.getComputedStyle(element);
        if (styles.display === 'none' || styles.visibility === 'hidden') {
          return;
        }

        // Only pinned elements stay in the way once the page scrolls
        if (styles.position !== 'fixed' && styles.position !== 'sticky') {
          return;
        }

        const rect = element.getBoundingClientRect();

        // Must be parked at the top of the viewport, and small enough to be chrome rather than
        // an off-canvas drawer or overlay that happens to be fixed
        if (rect.bottom <= 0 || rect.top > CHROME_MAX_TOP) {
          return;
        }

        if (rect.height > window.innerHeight * CHROME_MAX_RATIO) {
          return;
        }

        bottom = Math.max(bottom, rect.bottom);
      });
    });

    return bottom;
  }

  /**
   * The topmost product tile still intersecting the viewport, with its distance to the viewport
   * top. Tiles are in DOM order, so the first one with a bottom edge below 0 is the one we want.
   *
   * @returns {{handle: string, top: number}|null}
   */
  getAnchor() {
    const tiles = document.querySelectorAll(`${GRID_SELECTOR} ${TILE_SELECTOR}`);

    for (const tile of tiles) {
      if (tile.classList.contains('product-tile--placeholder')) continue;

      const rect = tile.getBoundingClientRect();
      if (rect.bottom > 0) {
        return {
          handle: tile.getAttribute('data-product-handle'),
          top: Math.round(rect.top),
        };
      }
    }

    return null;
  }

  /**
   * Where the saved entry says we should be, right now.
   *
   * @param {Object} entry - A stored scroll entry
   *
   * @returns {number|null} The scroll offset, or null when the tile we need isn't rendered yet
   */
  computeTarget(entry) {
    // 1. The product the user opened from the grid: park it just under the sticky chrome.
    //
    // Only when they left *from the grid*. Reaching a PDP through the search overlay (or a
    // recommendation, or quickview) means they never scrolled to that tile here, so coming
    // back should put them where they were — even though the product may well also be sitting
    // somewhere in this collection. Where we came back from otherwise wins over the recorded
    // click, which can be left over from an earlier visit.
    const left_from_grid = entry?.exited_via !== 'other';
    const target_handle = left_from_grid ? (this.returnedFromHandle || entry?.clicked_handle) : '';
    if (target_handle) {
      const tile = this.findTile(target_handle);

      if (tile) {
        // The tile was already on screen where they left: give them that exact view back rather
        // than moving it under the chrome (a first-row tile would otherwise scroll the page down)
        const anchor_target = this.anchorTarget(entry);
        if (anchor_target !== null && this.tileVisibleAt(tile, anchor_target)) {
          return anchor_target;
        }

        return Math.max(Math.round(PlpScrollRestore.absoluteTop(tile) - this.getChromeBottom() - TILE_GAP), 0);
      }

      // The tile is expected but hasn't rendered yet (later page, still loading): wait for it
      if (!entry?.anchor?.handle) {
        return null;
      }
    }

    // 2. Reproduce the viewport around the tile that was at the top of the screen
    if (entry?.anchor?.handle) {
      return this.anchorTarget(entry);
    }

    // 3. Nothing to anchor on
    return Math.max(Math.round(entry?.offset || 0), 0);
  }

  /**
   * @param {Object} entry - A stored scroll entry
   *
   * @returns {number|null} The offset that puts the saved anchor tile back where it was on screen,
   * or null when there's no anchor or its tile isn't rendered yet
   */
  anchorTarget(entry) {
    const anchor = entry?.anchor;
    const tile = anchor?.handle ? this.findTile(anchor.handle) : null;
    if (!tile) {
      return null;
    }

    return Math.max(Math.round(PlpScrollRestore.absoluteTop(tile) - anchor.top), 0);
  }

  /**
   * @param {Element} tile - A product tile
   * @param {number} scroll_top - A candidate scroll offset
   *
   * @returns {boolean} True when, at that offset, the tile's top is clear of the chrome and at least
   * half of it is on screen
   */
  tileVisibleAt(tile, scroll_top) {
    const top = PlpScrollRestore.absoluteTop(tile) - scroll_top;
    const height = tile.getBoundingClientRect().height;

    return top >= this.getChromeBottom() && (top + (height / 2)) <= window.innerHeight;
  }

  /* *** Saving *** */

  saveScrollPosition = () => {
    // While restoring, the position on screen is ours, not the user's: don't record it
    if (!this.enabled || this.isRestoring) {
      return;
    }

    // The search overlay sits over the PLP and locks the page behind it, so window.scrollY
    // here describes the overlay's state, not the shopper's place in the collection. Keep the
    // position we already saved.
    if (PlpScrollRestore.searchOverlayIsOpen()) {
      return;
    }

    this.updateEntry({
      offset: Math.round(window.scrollY || document.documentElement.scrollTop || 0),
      anchor: this.getAnchor(),
    });
  };

  onScroll = () => {
    window.clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(this.saveScrollPosition, SAVE_DEBOUNCE);
  };

  onVisibilityChange = () => {
    // iOS can discard the tab without ever firing pagehide
    if (document.visibilityState === 'hidden') {
      this.saveScrollPosition();
    }
  };

  /**
   * Remember how the shopper left for a PDP: from a tile in the grid, or from somewhere else.
   *
   * "Somewhere else" is the search overlay, a recommendation carousel, quickview — anywhere
   * they could reach a product without ever scrolling to it here. That distinction decides
   * whether coming back scrolls to the product or restores their place, and it can only be
   * made at click time: by the time we return, all we know is which PDP we came from.
   */
  onTileClick = (event) => {
    const link = event.target.closest('a[href*="/products/"]');
    if (!link) {
      return;
    }

    const in_grid = Boolean(link.closest(GRID_SELECTOR)) && !PlpScrollRestore.searchOverlayIsOpen();
    const handle = link.closest('[data-product-tile]')?.getAttribute('data-product-handle');

    if (!in_grid || !handle) {
      this.updateEntry({ exited_via: 'other', clicked_handle: '' });
      return;
    }

    this.updateEntry({
      exited_via: 'grid',
      clicked_handle: handle,
      offset: Math.round(window.scrollY || document.documentElement.scrollTop || 0),
      anchor: this.getAnchor(),
    });
  };

  /* *** Restoring *** */

  onPageShow = (event) => {
    // Served from the bfcache: the DOM is intact, so the tiles are already there and this
    // settles within a frame or two. If the browser kept the position, nothing moves.
    if (!event.persisted) {
      return;
    }

    // Nothing rewrote 'current-page' while we were frozen, so it still names the PDP
    this.captureReturnOrigin();

    const entry = this.readEntry();
    if (entry) {
      this.beginRestore(entry);
    }
  };

  beginRestore(entry) {
    this.pendingEntry = entry;
    this.isRestoring = true;
    this.hasApplied = false;
    this.corrections = 0;
    this.appliedTargets = [];
    this.applies = 0;

    document.addEventListener('astr-plp-tiles-built', this.onTilesBuilt);
    window.addEventListener('wheel', this.onUserInput, { capture: true, passive: true });
    window.addEventListener('touchstart', this.onUserInput, { capture: true, passive: true });
    window.addEventListener('keydown', this.onUserInput, { capture: true, passive: true });

    this.deadlineTimer = window.setTimeout(this.onDeadline, RESTORE_DEADLINE);

    // The tiles may already be there
    this.attemptRestore();
  }

  stopRestoring() {
    if (!this.isRestoring) {
      return;
    }

    // Consume the recorded exit: a handle left in the store would otherwise be reused as the
    // target of a later, unrelated back navigation
    if (this.hasApplied) {
      this.updateEntry({ clicked_handle: '', exited_via: '' });
    }

    // Detach from the grid we actually attached to, which may since have been replaced
    this.observedRoot?.removeEventListener('load', this.attemptRestore, true);

    this.isRestoring = false;
    this.pendingEntry = null;
    this.observedRoot = null;
    this.returnedFromHandle = '';
    this.corrections = 0;
    this.appliedTargets = [];
    this.applies = 0;

    window.clearTimeout(this.deadlineTimer);
    window.clearTimeout(this.quietTimer);
    this.deadlineTimer = null;
    this.quietTimer = null;

    if (this.correctionFrame !== null) {
      window.cancelAnimationFrame(this.correctionFrame);
      this.correctionFrame = null;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    document.removeEventListener('astr-plp-tiles-built', this.onTilesBuilt);
    window.removeEventListener('wheel', this.onUserInput, true);
    window.removeEventListener('touchstart', this.onUserInput, true);
    window.removeEventListener('keydown', this.onUserInput, true);
  }

  // Any deliberate scroll input means the user has taken over: never fight them
  onUserInput = (event) => {
    if (event.type === 'keydown' && ['Tab', 'Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) {
      return;
    }

    this.stopRestoring();
  };

  // A rebuilt grid is a fresh landing: give it a full correction budget again
  onTilesBuilt = () => {
    this.corrections = 0;
    this.appliedTargets = [];
    this.applies = 0;
    this.attemptRestore();
  };

  onDeadline = () => {
    // The tile never showed up (product gone, sold out and filtered away): fall back to the raw
    // offset, but only if the document is actually tall enough to honour it
    if (!this.hasApplied && this.pendingEntry) {
      const max_scroll = document.documentElement.scrollHeight - window.innerHeight;
      const offset = Math.round(this.pendingEntry.offset || 0);

      if (offset > 0 && max_scroll >= offset) {
        this.applyScroll(offset);
      }
    }

    this.stopRestoring();
  };

  applyScroll(target) {
    // 'auto', never 'smooth': a smooth scroll over a full PLP is the visible slide we're fixing
    window.scrollTo({ top: target, behavior: 'auto' });
    this.hasApplied = true;
  }

  /**
   * Landing changes the thing we measured to decide where to land: the header and filter bar
   * pin themselves on scroll, and pinning the filter bar pads [data-products]. Their scroll
   * handlers run before the next animation frame, so re-measuring there gets the settled
   * geometry.
   *
   * Capped, and stops as soon as the position holds, so a tile sitting exactly on the
   * pin/unpin boundary can't ping-pong.
   */
  scheduleCorrection() {
    if (this.corrections >= MAX_CORRECTIONS || this.correctionFrame !== null) {
      return;
    }

    this.corrections += 1;
    this.correctionFrame = window.requestAnimationFrame(() => {
      this.correctionFrame = null;
      this.attemptRestore();
    });
  }

  /**
   * The grid is built after we start, so hook the settle listeners up the first time it appears.
   */
  observeGrid() {
    const grid_root = this.getGridRoot();
    if (!grid_root || grid_root === this.observedRoot) {
      return;
    }

    this.observedRoot = grid_root;

    // Images don't bubble their load event, hence the capture phase
    grid_root.addEventListener('load', this.attemptRestore, true);

    if (typeof window.ResizeObserver !== 'undefined') {
      this.resizeObserver = new window.ResizeObserver(this.attemptRestore);
      this.resizeObserver.observe(grid_root);
    }
  }

  /**
   * Re-apply the saved position if we can, and keep the window open for the next render.
   * Called on every grid rebuild, image load and size change until we settle or time out.
   */
  attemptRestore = () => {
    if (!this.isRestoring || !this.pendingEntry) {
      return;
    }

    this.observeGrid();

    const target = this.computeTarget(this.pendingEntry);

    if (target !== null && Math.abs(window.scrollY - target) > LANDED_TOLERANCE) {
      // Back to the offset before the last one: the tile sits on the pin/unpin boundary, and each
      // landing flips the chrome, which flips the target. Resizes and image loads call us outside
      // scheduleCorrection()'s budget, so this would otherwise jitter until the deadline. Hold still.
      const [before_last] = this.appliedTargets;
      if (this.appliedTargets.length === 2 && Math.abs(target - before_last) <= LANDED_TOLERANCE) {
        this.stopRestoring();
        return;
      }

      // Hard cap on every path, not just the re-measure frames
      if (this.applies >= MAX_APPLIES) {
        this.stopRestoring();
        return;
      }

      this.applies += 1;
      this.appliedTargets = [this.appliedTargets[this.appliedTargets.length - 1], target].filter(value => typeof value === 'number');
      this.applyScroll(target);

      // The chrome we measured against moves once we land, so check again next frame
      this.scheduleCorrection();
    }

    // Settle once the layout has been quiet for a while, as long as we got a scroll in
    window.clearTimeout(this.quietTimer);
    this.quietTimer = window.setTimeout(() => {
      if (this.hasApplied) {
        this.stopRestoring();
      }
    }, SETTLE_QUIET_TIME);
  };
}

let instance = null;

/**
 * Set up saving and, when the user got here with the back button, restoring.
 *
 * Called from the PLP entrypoint only (main-collection.js). The scrollRestoration flag itself
 * is claimed earlier, inline in the head, because by the time this module runs the browser may
 * already have attempted its own restore.
 */
const initScrollRestore = () => {
  if (instance === null) {
    instance = new PlpScrollRestore();
  }
};

/**
 * True while we're re-applying a saved position. Callers that scroll the window on their own
 * (pagination resetting to the top, for instance) must stand down while this is true.
 *
 * @returns {boolean}
 */
const isRestoringPlpScroll = () => instance?.isRestoring === true;

export {
  initScrollRestore,
  isRestoringPlpScroll,
};
